import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ScrollText } from "lucide-react";

const ACTION_COLOR = {
  create: "bg-emerald-100 text-emerald-700 border-emerald-200",
  update: "bg-blue-100 text-blue-700 border-blue-200",
  delete: "bg-red-100 text-red-700 border-red-200",
  override_lock: "bg-amber-100 text-amber-700 border-amber-200",
};

export default function AuditLogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ page, limit: 50 });
      if (entityType) q.append("entity_type", entityType);
      if (action) q.append("action", action);
      const r = await api.get("/audit-log?" + q.toString());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, entityType, action]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><ScrollText className="h-6 w-6 text-emerald-600" /> Audit Log</h1>
        <p className="text-sm text-slate-500">Catatan semua perubahan penting oleh admin/sekretaris.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={entityType || "all"} onValueChange={(v) => { setPage(1); setEntityType(v === "all" ? "" : v); }}>
          <SelectTrigger className="w-[220px]"><SelectValue placeholder="Jenis Entitas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua entitas</SelectItem>
            <SelectItem value="nilai_ujian">Nilai</SelectItem>
            <SelectItem value="absensi_santri">Absensi Santri</SelectItem>
            <SelectItem value="absensi_ustadz">Absensi Ustadz</SelectItem>
            <SelectItem value="pembayaran">Pembayaran</SelectItem>
            <SelectItem value="assignment">Assignment</SelectItem>
            <SelectItem value="jadwal_pelajaran">Jadwal</SelectItem>
            <SelectItem value="catatan_wali">Catatan Wali</SelectItem>
            <SelectItem value="settings">Settings</SelectItem>
          </SelectContent>
        </Select>
        <Select value={action || "all"} onValueChange={(v) => { setPage(1); setAction(v === "all" ? "" : v); }}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Aksi" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua aksi</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="override_lock">Override Lock</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-500">Belum ada catatan audit.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(it => (
            <Card key={it.id}>
              <CardContent className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={ACTION_COLOR[it.action] || "bg-slate-100 text-slate-700"}>{it.action}</Badge>
                      <span className="text-sm font-medium text-slate-800">{it.entity_type}</span>
                      <span className="text-xs text-slate-400">#{it.entity_id?.slice(0, 8)}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      <b>{it.actor_name || "?"}</b> ({it.actor_role}) · {new Date(it.created_at).toLocaleString()}
                    </p>
                    {it.reason && <p className="text-xs text-amber-700 mt-1">Alasan: {it.reason}</p>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setExpanded(e => ({ ...e, [it.id]: !e[it.id] }))}>
                    {expanded[it.id] ? "Sembunyikan" : "Detail"}
                  </Button>
                </div>
                {expanded[it.id] && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    <div className="bg-red-50 border border-red-100 rounded p-2">
                      <p className="font-semibold text-red-700 mb-1">Sebelum</p>
                      <pre className="whitespace-pre-wrap overflow-auto">{JSON.stringify(it.before, null, 2)}</pre>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-100 rounded p-2">
                      <p className="font-semibold text-emerald-700 mb-1">Sesudah</p>
                      <pre className="whitespace-pre-wrap overflow-auto">{JSON.stringify(it.after, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          <div className="flex justify-between items-center pt-2">
            <span className="text-sm text-slate-500">Total: {total}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <span className="text-sm">Hal {page}</span>
              <Button size="sm" variant="outline" disabled={page * 50 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
